import { cacheTiers } from "@/api/queryClient";
import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getMetricDrillDown } from "@/service/reqUrl.service";

import type { MetricDrillDownParams, MetricDrillDownRow } from "./types";

interface UseMetricDrillDownResult {
  /** Normalized record list. Empty array = the SP returned no records (an empty
   *  state), which is different from `loading` or `error`. */
  rows: MetricDrillDownRow[];
  loading: boolean;
  error: string | null;
}

/** Pull the record array out of whatever the SP returned: a bare array, or a
 *  `FOR JSON` document whose single key holds the rows. Columns are dynamic, so
 *  nothing here inspects or renames them. */
function toRows(data: unknown): MetricDrillDownRow[] {
  if (Array.isArray(data)) {
    return data.filter((r): r is MetricDrillDownRow => !!r && typeof r === "object");
  }
  if (data && typeof data === "object") {
    for (const v of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        return v.filter((r): r is MetricDrillDownRow => !!r && typeof r === "object");
      }
    }
  }
  return [];
}

/**
 * Records behind one All Metrics cell, via POST /all-metric/drill-down
 * (`GetMetricDrillDown`). Fired when the user opens the Drill Down modal on a
 * metric whose interval row reports `HasDrillDown = 1`.
 *
 * `params` is null while no modal is open, which keeps the hook idle. Re-fires
 * whenever the metric, the clicked interval, or any applied filter changes.
 *
 * CACHE (tier D): record sets can be large and are opened far less often than
 * they are closed, so these entries are evicted after 10 minutes rather than the
 * 30 the metric queries get.
 */
export function useMetricDrillDown(
  params: MetricDrillDownParams | null,
): UseMetricDrillDownResult {
  const userKey = useUserKey();

  const { data, loading, error } = useApiQuery<unknown, MetricDrillDownRow[]>({
    queryKey: qk.records(userKey, params),
    url: getMetricDrillDown,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch drill-down records",
    select: toRows,
    ...cacheTiers.records,
  });

  return { rows: data ?? [], loading, error };
}
